/**
 * Reed.ai Design System — Select
 *
 * Styled dropdown built on native <select> for accessibility.
 * Source: Figma "Reed.ai - Global components" — A / Form fields
 * Node: 2077:1482
 */

import React, { useId, useState } from 'react';
import { cn } from '../../utils/cn';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  optional?: boolean;
  options: SelectOption[];
  helpText?: string;
  errorText?: string;
  size?: 'desktop' | 'mobile';
  className?: string;
}

function ChevronIcon({ direction = 'down' }: { direction?: 'down' | 'up' }) {
  return (
    <svg aria-hidden="true" fill="none" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
      <path
        d={direction === 'down' ? 'M6 9l6 6 6-6' : 'M6 15l6-6 6 6'}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}

/**
 * Reed.ai Select
 *
 * @example
 * <Select
 *   label="Seniority"
 *   options={[
 *     { value: 'junior', label: 'Junior (0–2 years)' },
 *     { value: 'mid',    label: 'Mid (3–5 years)' },
 *     { value: 'senior', label: 'Senior (6+ years)' },
 *   ]}
 * />
 *
 * // With error
 * <Select label="Role type" options={opts} errorText="Please select a role type" />
 */
export function Select({
  label = 'Label',
  optional = false,
  options,
  helpText,
  errorText,
  size = 'desktop',
  className,
  id: idProp,
  disabled,
  value,
  defaultValue,
  onChange,
  ...rest
}: SelectProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const hasError = !!errorText;
  const helpId = helpText || errorText ? `${id}-help` : undefined;
  const [internalValue, setInternalValue] = useState<React.SelectHTMLAttributes<HTMLSelectElement>['value']>(defaultValue ?? '');
  const selectedValue = value !== undefined ? value : internalValue;
  const selectedLabel = options.find((opt) => String(opt.value) === String(selectedValue))?.label;
  // Base "has value" on the raw value, not on finding a matching option label — a
  // controlled value that isn't (yet) in `options` (async load, removed option)
  // must still render as populated rather than collapsing to the empty placeholder.
  const hasValue = selectedValue != null && String(selectedValue) !== '';
  const isInactive = disabled;
  const isCompactEmpty = !hasValue && !hasError;

  const borderColor = hasError ? 'border-system-error' : hasValue ? 'border-neutral-20' : 'border-border-tertiary';
  const fieldHeight = isCompactEmpty
    ? (size === 'mobile' ? 'h-[calc(var(--space-10)+var(--space-3))]' : 'h-[calc(var(--space-10)+var(--space-4))]')
    : 'h-20';

  return (
    <div className={cn('relative w-full flex flex-col gap-3', className)}>
      <div
        className={cn(
          'relative flex items-center bg-white border',
          isCompactEmpty ? 'rounded-s' : 'rounded-none',
          borderColor,
          fieldHeight,
          'px-4',
          isInactive && 'cursor-not-allowed',
        )}
      >
        {isCompactEmpty ? (
          <label htmlFor={id} className={cn('pointer-events-none font-body text-body-md', isInactive ? 'text-text-secondary' : 'text-neutral-70')}>
            {label}{optional && <span className="ml-1">(Optional)</span>}
          </label>
        ) : (
          <div className="pointer-events-none flex min-w-0 flex-1 flex-col gap-2">
            <label htmlFor={id} className="font-body text-body-sm text-neutral-90">
              {label}{optional && <span className="ml-1">(optional)</span>}
            </label>
            <span className="font-body text-body-md text-neutral-70">
              {selectedLabel ?? String(selectedValue)}
            </span>
          </div>
        )}

        <select
          {...rest}
          id={id}
          disabled={!!isInactive}
          aria-invalid={hasError || undefined}
          aria-describedby={helpId}
          value={selectedValue}
          onChange={(event) => {
            if (value === undefined) setInternalValue(event.target.value);
            onChange?.(event);
          }}
          className={cn(
            'absolute inset-0 h-full w-full appearance-none border-none bg-transparent opacity-0 outline-none',
            isInactive && 'cursor-not-allowed',
          )}
        >
          <option value="" disabled hidden></option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>
        {!isCompactEmpty && (
          <span className="pointer-events-none absolute right-4 text-neutral-90">
            <ChevronIcon />
          </span>
        )}
      </div>

      {(helpText || errorText) && (
        <p id={helpId} className={cn('px-4 font-body text-body-md', hasError ? 'text-system-error' : 'text-neutral-70')}>
          {errorText ?? helpText}
        </p>
      )}
    </div>
  );
}

export default Select;
